import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  CardHeader,
  Grid,
  Container,
  Breadcrumbs,
  Stack,
  Typography,
  Link as MuiLink
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ScrollToTop from "react-scroll-to-top";
import Loader from "react-js-loader";
import moment from "moment";
import makeStyles from "@material-ui/styles/makeStyles";
import { getPerkFormData, getPerkDataByUsername, increasePerkFormVisitCount } from "../../redux/actions/perk";
import config from "../../config/config";
import ViewPerk from "./ViewPerk";
import LoadingPage from "../auth/LoadingPage";


const PreviewPerks = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { username } = useParams();
  const [user, setUser] = useState({});
  const [showViewPerk, setShowViewPerk] = useState(false);
  const [selectedPerk, setSelectedPerk] = useState();
  const [loading, setLoading] = useState(true);

  const { emailNotification, perkData = [] } = useSelector(state => ({
    emailNotification: state.book.emailNotification,
    perkData: state.perk.perkData
  }));

  const useStyles = makeStyles({
    title: {
      color: theme.palette.mode === 'light' ? '#b5b5c3' : '#474761',
      fontSize: '13px',
      fontWeight: '500',
      lineHeight: '18px',
      '&:hover': {
        color: '#009ef7 !important',
      }
    },
    subheader: {
      color: theme.palette.mode === 'light' ? '#3f4254' : '#cdcdde',
      fontSize: '12.35px',
      fontWeight: '600',
      lineHeight: '18px',
    }
  });
  const styles = useStyles();

  useEffect(() => {
    onInit();
  }, []);

  const onInit = async () => {
    setLoading(true);
    await dispatch(getPerkDataByUsername(username));
    const res = await dispatch(getPerkFormData(username));
    setUser({
      firstName: res.firstName,
      lastName: res.lastName,
      bookFormId: res.bookFormId,
      avatar: res.avatar,
    });
    setLoading(false);
  }

  function getDaysRemainingText(expiryDate) {
    const daysRemaining = moment(expiryDate).diff(moment(), 'days');
    return moment(expiryDate).isBefore(moment())
      ? 'Expired'
      : daysRemaining + ' day' + (daysRemaining > 1 ? 's ' : ' ') + 'left';
  }

  const onOpenViewPerk = () => setShowViewPerk(true);
  const onCloseViewPerk = () => setShowViewPerk(false);

  const onViewPerk = async (perk) => {
    setSelectedPerk(perk);
    onOpenViewPerk();
    await dispatch(increasePerkFormVisitCount(perk.id));
  }

  const onActivatePerk = () => {
    const newwindow = window.open(`${config.CLIENT_BASE_URL}/${username}/book-perk`, "mywindow", "location=1, status=1, scrollbars=1, width=1400, height=880");
    newwindow.moveTo((window.screen.width - 1400) / 2, (window.screen.height - 880) / 2);
  }

  const onMakeBooking = () => {
    const newwindow = window.open(`${config.CLIENT_BASE_URL}/${username}/book`, "mywindow", "location=1, status=1, scrollbars=1, width=1400, height=880");
    newwindow.moveTo((window.screen.width - 1400) / 2, (window.screen.height - 880) / 2);
  }

  const onCustomURLClicked = (value) => {
    if (!/^https?:\/\//i.test(value)) {
      value = 'http://' + value;
    }
    window.open(value, "_blank");
  }

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <Grid
      className="page-content"
      sx={{
        backgroundImage: `url(${config.SERVER_BASE_URL}/${emailNotification?.perkFeaturedImage})`,
        backgroundColor: theme.palette.mode === 'light' ? '#f5f8fa' : '#151521'
      }}
    >
      <Container maxWidth="lg">
        <Grid sx={{ py: 5, marginInline: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {emailNotification?.avatar?.includes("https://gravatar.com/") ?
            <Avatar
              sx={{
                height: '150px',
                width: '150px',
                color: '#fff',
                backgroundColor: 'tomato',
                fontSize: '75px',
                fontWeight: 600,
                margin: 'auto',
              }}
            >
              {emailNotification?.firstName ? emailNotification?.firstName[0] : ''}
            </Avatar> :
            <Box component="img" src={`${config.SERVER_BASE_URL}/${emailNotification?.avatar}`} sx={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover' }} />
          }
          <Typography sx={{ color: '#fff', fontSize: '1.8rem', fontWeight: 600, textAlign: 'center', mt: 2 }}>{emailNotification?.perkHeading}</Typography>
          <Typography sx={{ color: theme.palette.mode === 'light' ? '#fff' : '#fff', fontSize: '0.9rem', fontWeight: 400, textAlign: 'center', maxWidth: '600px' }}>
            {emailNotification?.perkMessage}
          </Typography>
          <Breadcrumbs aria-label="breadcrumb" separator="●" sx={{ mt: 2, display: 'flex', color: theme.palette.mode === 'light' ? '#b5b5c3' : '#474761', justifyContent: 'center', '&:hover': { cursor: 'pointer' } }}>
            {(emailNotification.customLinks || []).map((item, index) => (
              <MuiLink key={index} onClick={() => onCustomURLClicked(item.value)} color={theme.palette.mode === 'light' ? '#b5b5c3' : '#474761'} underline="none" sx={{ fontSize: '0.8rem' }}>{item.label}</MuiLink>
            ))}
          </Breadcrumbs>
        </Grid>
        {loading ?
          <Loader type="bubble-loop" bgColor={"#2196f3"} color={'#2196f3'} size={100} style={{ alignSelf: 'center' }} /> :
          <Grid
            container
            spacing={2}
          >
            {perkData.length > 0 ? 
              perkData.map((perk, index) => (
                <Grid
                  key={index}
                  item
                  md={12}
                  lg={4}
                  xs={12}
                >
                  <Container sx={{ backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d', padding: '0px !important', borderRadius: '8px' }}>
                    <Box className="overlay" onClick={() => onViewPerk(perk)}>
                      <Box
                        sx={{
                          backgroundImage: `url(${config.SERVER_BASE_URL}/${perk.featuredImage})`,
                          backgroundRepeat: "no-repeat",
                          backgroundSize: "cover",
                          backgroundPosition: 'center',
                          minHeight: '250px',
                          borderRadius: '8px'
                        }}
                      />
                      <Box component="div" className="overlay-layer">
                        <VisibilityIcon sx={{ color: '#fff' }} />
                      </Box>
                    </Box>
                    <Box sx={{ padding: '30px' }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <CardHeader
                          classes={{ title: styles.title, subheader: styles.subheader }}
                          avatar={user == null || user?.avatar?.includes("https://gravatar.com/") ?
                            <Avatar sx={{ bgcolor: 'tomato', width: '35px', height: '35px' }} alt="User Name" variant='rounded'>
                              {user?.firstName[0]}
                            </Avatar> :
                            <Box
                              component="img"
                              src={`${config.SERVER_BASE_URL}/${user.avatar}`}
                              alt="avatar"
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                              }}
                            />
                          }
                          title="Requirement"
                          subheader={perk.requirement}
                          sx={{ padding: 0 }}
                        />
                      </Stack>
                      <Typography
                        onClick={() => onViewPerk(perk)}
                        sx={{
                          color: theme.palette.mode === 'light' ? '#181c32' : '#fff',
                          fontSize: '28px',
                          fontWeight: 700,
                          lineHeight: '33px',
                          cursor: 'pointer',
                          whiteSpace: 'pre-line',
                          mt: 2 }}>
                        {perk.title}
                      </Typography>
                      <Typography sx={{ color: theme.palette.mode === 'light' ? '#181c32' : '#fff', fontSize: '14px', mt: 1 }}>{getDaysRemainingText(perk.expiryDate)} • {perk.location}</Typography>
                      <Typography sx={{ color: theme.palette.mode === 'light' ? '#5e6278' : '#92929f', fontSize: '14px', fontWeight: 500, whiteSpace: 'pre-line', mt: 2 }}>
                        {perk.summary}
                      </Typography>
                      <Stack direction="row" sx={{ mt: 2 }}>
                        <Button
                          onClick={() => onActivatePerk(perk)}
                          color="primary"
                          variant="contained"
                          sx={{
                            width: '150px',
                            fontSize: '14px',
                            fontWeight: 600,
                            textTransform: 'none',
                            color: '#fff',
                            padding: '11px 20px',
                            borderRadius: '6px'
                          }}
                        >
                          Activate Perk
                        </Button>
                        <Button
                          onClick={() => onViewPerk(perk)}
                          sx={{
                            border: '1px solid',
                            borderColor: theme.palette.mode === 'light' ? '#e4e6ef' : '#323248',
                            fontSize: '14px',
                            fontWeight: 500,
                            textTransform: 'none',
                            color: theme.palette.mode === 'light' ? '#181c32' : '#fff',
                            padding: '11px 20px',
                            borderRadius: '6px',
                            ml: 1,
                            "&:hover": { backgroundColor: 'transparent' }
                          }}
                        >
                          Details
                        </Button>
                      </Stack>
                    </Box>
                  </Container>
                </Grid>
              )) : (
              <Container sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 20 }}>
                <Button
                  onClick={onMakeBooking}
                  color="primary"
                  variant="contained"
                  sx={{
                    width: '200px',
                    fontSize: '14px',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#fff',
                    padding: '11px 20px',
                    borderRadius: '6px'
                  }}
                >
                  Make A Booking
                </Button>
                <Typography sx={{ mt: 5, fontWeight: 700, fontSize: '1.2rem', color: theme.palette.mode ==="light" ? "#3f4254" : "#fff" }}>
                  There are active Perks at the moment. Please check back soon!
                </Typography>
              </Container>
            )}
          </Grid>
        }
        <ViewPerk
          visible={showViewPerk}
          onClose={onCloseViewPerk}
          perk={selectedPerk}
        />
        <ScrollToTop
          smooth
          color="#009EF7"
        />
      </Container>
    </Grid>
  )
}

export default PreviewPerks;
